import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-full max-w-2xl mx-auto grid gap-2" }
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col items-center p-6 text-center"
}
const _hoisted_4 = { class: "p-4 rounded-full bg-white/10 mb-4" }
const _hoisted_5 = {
  key: 1,
  class: "w-full p-4"
}
const _hoisted_6 = { class: "flex flex-wrap gap-3 items-center justify-center" }
const _hoisted_7 = ["src"]
const _hoisted_8 = ["src"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "absolute top-1 left-1 w-5 h-5 flex items-center justify-center bg-primary rounded-full text-xs text-white" }
const _hoisted_11 = {
  key: 2,
  class: "absolute inset-x-0 bottom-0 bg-black/70 p-2"
}
const _hoisted_12 = { class: "block text-center text-sm text-white mt-1" }

import { ref, computed, watch } from 'vue'
import { LinkIcon, XIcon, PlusIcon, UploadIcon } from 'lucide-vue-next'
import { Label } from '../label';
import { useToast } from '../toast';

interface Props {
  label?: string
  modelValue?: File[] | null // Para v-model com múltiplos arquivos
  previews?: string[] // URLs das prévias existentes (opcional)
  maxFiles?: number
  maxSizeImage?: number // em MB
  maxSizeVideo?: number // em MB
  required?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MultipleFilesUpload',
  props: {
    label: { default: '' },
    modelValue: { default: () => [] },
    previews: { default: () => [] },
    maxFiles: { default: 10 },
    maxSizeImage: { default: 5 },
    maxSizeVideo: { default: 50 },
    required: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'update:previews'],
  setup(__props: any, { emit: __emit }) {

const { toast } = useToast()

const props = __props

const emit = __emit

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const selectedFiles = ref<File[]>(Array.isArray(props.modelValue) ? [...props.modelValue] : [])
const fileUrls = ref<string[]>([])
const uploading = ref(false)
const progress = ref(0)

// Computed para exibir arquivos (seja do preview ou selecionados)
const filesToDisplay = computed(() => {
  return [...(props.previews || []), ...selectedFiles.value]
})

// Atualizar quando o modelValue mudar externamente
watch(() => props.modelValue, (newValue) => {
  if (newValue && Array.isArray(newValue)) {
    selectedFiles.value = [...newValue]
    updateFileUrls()
  } else {
    selectedFiles.value = []
    fileUrls.value = []
  }
}, { deep: true })

// Gerar URLs para os arquivos selecionados
function updateFileUrls() {
  // Limpar URLs antigos para evitar vazamento de memória
  fileUrls.value.forEach(url => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  })
  
  fileUrls.value = selectedFiles.value.map(file => URL.createObjectURL(file))
}

// Verificar se é uma imagem
function isImageFile(file: File | string): boolean {
  if (typeof file === 'string') {
    return file.startsWith('data:image') || /\.(jpeg|jpg|gif|png|avif|webp)$/i.test(file)
  }
  return file.type.startsWith('image/')
}

// Obter URL para exibição
function getFileUrl(file: File | string): string {
  if (typeof file === 'string') {
    return file
  }
  
  const index = selectedFiles.value.indexOf(file)
  if (index !== -1 && index < fileUrls.value.length) {
    return fileUrls.value[index]
  }
  return ''
}

const triggerFileInput = () => {
  if ((selectedFiles.value.length + (props.previews?.length ?? 0)) < props.maxFiles) {
    fileInput.value?.click()
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  
  if (!files || files.length === 0) return
  
  // Converter FileList para array e processar
  const fileArray = Array.from(files)
  processFiles(fileArray)
  
  // Limpar input para permitir selecionar o mesmo arquivo novamente
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  
  if (!event.dataTransfer?.files || event.dataTransfer.files.length === 0) return
  
  const fileArray = Array.from(event.dataTransfer.files)
  processFiles(fileArray)
}

const processFiles = (files: File[]) => {
  // Verificar se não excede o limite máximo
  const remainingSlots = props.maxFiles - (selectedFiles.value.length + (props.previews?.length ?? 0))
  if (remainingSlots <= 0) {
    toast({
      variant: 'destructive',
      description: `Você atingiu o limite máximo de ${props.maxFiles} arquivos.`
    })
    return
  }
  
  // Limitar ao número de slots disponíveis
  const filesToProcess = files.slice(0, remainingSlots)
  
  // Validar cada arquivo
  const validFiles: File[] = []
  
  for (const file of filesToProcess) {
    // Verificar tipo
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast({
        variant: 'destructive',
        description: 'Por favor, selecione apenas arquivos de imagem ou vídeo.'
      })
      continue
    }
    
    // Verificar tamanho
    const maxSize = file.type.startsWith('image/') ? props.maxSizeImage : props.maxSizeVideo
    if (file.size > maxSize * 1024 * 1024) {
      toast({
        variant: 'destructive',
        description: `O arquivo ${file.name} excede o tamanho máximo de ${maxSize}MB.`
      })
      continue
    }

    validFiles.push(file)
  }
  
  if (validFiles.length > 0) {
    // Adicionar aos arquivos selecionados
    selectedFiles.value = [...selectedFiles.value, ...validFiles]
    updateFileUrls()
    emit('update:modelValue', selectedFiles.value)
    simulateUpload()
  }
}

const removeFile = (index: number) => {
  if (filesToDisplay.value[index].toString().includes('http')) {
    const previews = props.previews?.filter(preview => preview !== filesToDisplay.value[index])
    emit('update:previews', previews)
    return
  }
  
  // Remover URL do objeto
  if ((index - (props.previews?.length ?? 0)) < fileUrls.value.length) {
    URL.revokeObjectURL(fileUrls.value[(index - (props.previews?.length ?? 0))])
  }
  
  // Remover arquivo
  selectedFiles.value.splice((index - (props.previews?.length ?? 0)), 1)
  fileUrls.value.splice((index - (props.previews?.length ?? 0)), 1)
  
  emit('update:modelValue', selectedFiles.value.length > 0 ? selectedFiles.value : null)
}

const simulateUpload = () => {
  uploading.value = true
  progress.value = 0
  
  const interval = setInterval(() => {
    progress.value += 5
    if (progress.value >= 100) {
      clearInterval(interval)
      uploading.value = false
    }
  }, 50)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Label), null, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
        (_ctx.required)
          ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", {
      class: _normalizeClass(["relative min-h-[200px] rounded-lg border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center cursor-pointer", [ 
        isDragging.value && !_ctx.previews
          ? 'border-emerald-400 bg-emerald-400/5' 
          : 'border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10',
        (selectedFiles.value.length > 0 || _ctx.previews) ? 'border-solid' : 'border-dashed'
      ]]),
      onDragenter: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (isDragging.value = true), ["prevent"])),
      onDragleave: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (isDragging.value = false), ["prevent"])),
      onDragover: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
      onDrop: _withModifiers(handleDrop, ["prevent"]),
      onClick: triggerFileInput
    }, [
      _createElementVNode("input", {
        type: "file",
        ref_key: "fileInput",
        ref: fileInput,
        class: "hidden",
        onChange: handleFileSelect,
        accept: "image/*,video/mp4",
        multiple: ""
      }, null, 544),
      (selectedFiles.value.length === 0 && !_ctx.previews?.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(LinkIcon), { class: "w-8 h-8 text-primary" })
            ]),
            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-white" }, [
              _createTextVNode(" Selecione imagens ou vídeos "),
              _createElementVNode("span", { class: "block text-sm text-white/60" }, " ou arraste e solte aqui ")
            ], -1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filesToDisplay.value, (file, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "relative w-24 h-24 border border-white/20 rounded-lg overflow-hidden group"
                }, [
                  (isImageFile(file))
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: getFileUrl(file),
                        alt: "Preview",
                        class: "w-full h-full object-cover"
                      }, null, 8, _hoisted_7))
                    : (_openBlock(), _createElementBlock("video", {
                        key: 1,
                        src: getFileUrl(file),
                        class: "w-full h-full object-cover"
                      }, null, 8, _hoisted_8)),
                  _createElementVNode("button", {
                    onClick: _withModifiers(($event: any) => (removeFile(index)), ["stop"]),
                    class: "absolute top-1 right-1 p-1 rounded-full bg-red-500/80 opacity-0 group-hover:opacity-100 transition-opacity",
                    "aria-label": "Remover mídia"
                  }, [
                    _createVNode(_unref(XIcon), { class: "w-3 h-3 text-white" })
                  ], 8, _hoisted_9),
                  _createElementVNode("div", _hoisted_10, _toDisplayString(index + 1), 1)
                ]))
              }), 128)),
              ((selectedFiles.value.length + (_ctx.previews?.length ?? 0)) > 0 && (selectedFiles.value.length + (_ctx.previews?.length ?? 0)) < _ctx.maxFiles)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    onClick: _withModifiers(triggerFileInput, ["stop"]),
                    class: "w-24 h-24 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-white/40 hover:bg-white/10"
                  }, [
                    _createVNode(_unref(PlusIcon), { class: "w-8 h-8 text-white/60" })
                  ]))
                : _createCommentVNode("", true)
            ])
          ])),
      (uploading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("div", {
              class: "h-1 bg-emerald-400 transition-all duration-300",
              style: _normalizeStyle({ width: `${progress.value}%` })
            }, null, 4),
            _createElementVNode("span", _hoisted_12, _toDisplayString(progress.value) + "% ", 1)
          ]))
        : _createCommentVNode("", true)
    ], 34)
  ]))
}
}

})